import { isDefined } from "@clipboard-health/util-ts";
import { useCurrentDate } from "@src/appV2/lib/utils/useCurrentDate";
import { getClockInTimingInfo } from "@src/appV2/redesign/utils/clockInTiming";
import { type ShiftStateData } from "@src/appV2/Shifts/Shift/ShiftState/types";
import { isAfter, subMinutes } from "date-fns";
import { useMemo } from "react";

const DEFAULT_BUFFER_MINUTES = 15;

export interface UseEnableGeofenceTrackingProps {
  shiftStart: string | undefined;
  shiftState: ShiftStateData | undefined;
  bufferMinutes?: number;
}

/**
 * Hook to determine when Geofence should be enabled based on clock-in timing.
 *
 * Geofence is enabled with a buffer before the earliest possible clock-in time
 * to ensure orders are ready when users can actually clock in.
 *
 * @param shiftStart - The shift start time in ISO format
 * @param shiftState - The shift state containing clock-in configuration
 * @param bufferMinutes - Minutes before earliest clock-in time to start Geofence (default: 15)
 * @returns boolean indicating whether Geofence should be enabled
 */
export function useEnableGeofenceTracking({
  shiftStart,
  shiftState,
  bufferMinutes = DEFAULT_BUFFER_MINUTES,
}: UseEnableGeofenceTrackingProps): boolean {
  const currentDate = useCurrentDate(60_000);

  return useMemo(() => {
    if (!isDefined(shiftStart) || !isDefined(shiftState)) {
      return false;
    }

    // Don't enable tracking if shift is over
    if (shiftState.metadata.isShiftOver) {
      return false;
    }

    const { earliestClockInTime } = getClockInTimingInfo({
      shiftStart,
      shiftState,
      currentDate,
    });

    if (!earliestClockInTime) {
      return false;
    }

    // Start HyperTrack with a buffer before the earliest clock-in time to ensure orders are ready
    const hyperTrackStartTime = subMinutes(earliestClockInTime, bufferMinutes);

    return (
      isAfter(currentDate, hyperTrackStartTime) ||
      currentDate.getTime() === hyperTrackStartTime.getTime()
    );
  }, [shiftStart, shiftState, currentDate, bufferMinutes]);
}
